/* eslint-disable no-unused-vars */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Input, Modal, Result, Button, message, Space, Switch } from 'antd';
import { useReactToPrint } from 'react-to-print';
import generatePDF from 'react-to-pdf';
import moment from 'moment';
import { FilterOutlined, ExportOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { AntdTable } from 'components/custom-table/Style';
import { ButtonWrapper } from 'components';
import { ACL } from 'components/common';
import { Box, Text } from 'atoms';
import { getListAction, exportSlice } from 'store/pageSlice';
import { getUIVersion } from 'utils/router-static-data';
import { deleteConsignment } from '../lr-api';
import { initialPaginateConfig } from 'utils';
import { GenerateLRList } from './LRTableConfig';
import { LRCopy } from '../LRView/LRview';
import { LRPrint } from '../LRView/components/LRPrint';
import { getTerminalDates } from './functions/TerminalDateRanges';
import { QuickFilters, CountStatus, DateRangePicker } from './components';
import './customCSS.css';

const { Search } = Input;
const SCOPEDDAYS = 30;

const pdfOptions = {
	method: 'save',
	resolution: 1,
	page: {
		margin: 5,
		format: 'A4',
		orientation: 'portrait',
	},
	canvas: {
		mimeType: 'image/jpeg',
		qualityRatio: 1,
	},
	overrides: {
		pdf: {
			compress: true,
		},
		canvas: {
			useCORS: true,
		},
		unit: 'mm',
	},
};

const LtSearchBar = styled(Search)`
	border: none;
`;

const normalizeV1GoodsItem = (item = {}) => ({
	goods_description: item.goods_description || item.name || '',
	goods_quantity: item.goods_quantity || item.quantity || '',
	goods_weight: item.goods_weight || item.weight || '',
	goods_uom: item.goods_uom || item.model || '',
	goods_length:
		item.goods_length ||
		(typeof item.dimensions === 'string' ? item.dimensions.split('x')[0] : '') ||
		'',
	goods_width:
		item.goods_width ||
		(typeof item.dimensions === 'string' ? item.dimensions.split('x')[1] : '') ||
		'',
	goods_height:
		item.goods_height ||
		(typeof item.dimensions === 'string' ? item.dimensions.split('x')[2] : '') ||
		'',
});

const transformGoodsDetailsToV1 = (goodsDetails) => {
	if (!goodsDetails) return [];
	if (Array.isArray(goodsDetails)) return goodsDetails.map(normalizeV1GoodsItem);
	if (goodsDetails.items && Array.isArray(goodsDetails.items)) {
		return goodsDetails.items.map(normalizeV1GoodsItem);
	}
	return [];
};

const transformConsignmentNoteRow = (note = {}) => ({
	...note,
	project: note.project || note.project_name || note.project_location_code || '',
	vendor: note.vendor || note.vendor_name || '',
	goods_details: transformGoodsDetailsToV1(note.goods_details),
});

export function LRListPage() {
	const isV2UI = getUIVersion() === 'new';
	const refreshRate = 5; // refresh data only after 5 minute from previous request
	const [selectLRDetails, setSelectedLRDetails] = useState({});
	const [statusCount, setStatusCount] = useState({
		issued: 0,
		cancelled: 0,
	});
	const [filterChecked, setFilterChecked] = useState(false);
	const [areFiltersApplied, setAreFiltersApplied] = useState(false);
	const [lastUpdate, setLastUpdate] = useState(moment());
	const [showPrint, setShowPrint] = useState(false);
	const [colFilters, setColFilters] = useState({});
	const [selectQfilt, setQfilt] = useState('');
	const [pagefilter, setFilters] = useState({});
	const defaultDateRange = getTerminalDates({ init: true });
	const [dateRange, setDateRange] = useState(defaultDateRange);
	const [dataDateRange, setDataDateRange] = useState(defaultDateRange);
	const [isModalOpen, setModalOpen] = useState(false);
	const [printVisible, setPrintVisible] = useState(false);
	const [warnCancel, setWarnCancel] = useState(false);
	const [isPDFPrint, setPDFPrint] = useState(false);
	const [selectedID, setSelectedID] = useState();
	const LRPrintRef = useRef();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [rowData, setRowData] = useState();
	const [filteredData, setFilteredData] = useState(rowData?.rows);
	const [queryParams, setQueryParams] = useState({
		...initialPaginateConfig,
		from_date: dateRange.startDate,
		to_date: dateRange.endDate,
		search_type: 'consignment_note',
		limit: 100000,
	});

	const printComponent = useReactToPrint({
		content: () => LRPrintRef.current,
		onAfterPrint: () => setModalOpen(false),
		pageStyle: '@page { size: 42cm 56cm }',
	});

	const printLR = useCallback(() => printComponent(), [printComponent]);

	function checkRefresh() {
		return moment().diff(lastUpdate, 'minutes') > refreshRate;
	}

	function FilterData(resetQuickFilter = false, newDateRange = null) {
		// Use the passed date range or fall back to current state
		const currentDateRange = newDateRange || dateRange;

		if (
			currentDateRange.startDate.isBetween(
				dataDateRange.startDate,
				dataDateRange.endDate,
				'days',
				'[]'
			) &&
			currentDateRange.endDate.isBetween(
				dataDateRange.startDate,
				dataDateRange.endDate,
				'days',
				'[]'
			) &&
			!checkRefresh()
		) {
			const reducedData = rowData.filter((item) =>
				moment(item.created_at).isBetween(
					currentDateRange.startDate,
					currentDateRange.endDate,
					'days',
					'[]'
				)
			);
			setFilteredData(reducedData);
		} else {
			setQueryParams({
				...queryParams,
				from_date: currentDateRange.startDate.startOf('days'),
				to_date: currentDateRange.endDate.endOf('days'),
			});
			setDataDateRange({ ...currentDateRange });
		}
		// Reset quick filter only when manually applying filters
		if (resetQuickFilter) {
			setQfilt('');
		}
		setFilterChecked(false);
	}

	const getData = useCallback(() => {
		dispatch(getListAction(queryParams)).then((res) => {
			const rows = isV2UI ? (res?.rows || []).map(transformConsignmentNoteRow) : res?.rows;
			setRowData(rows);
			setFilteredData(rows);
		});
		setLastUpdate(new Date());
	}, [dispatch, isV2UI, queryParams]);

	useEffect(() => {
		getData();
	}, [queryParams, getData]);

	useEffect(() => {
		const filterSet = {
			client_name: [],
			project: [],
			vendor: [],
			sender_name: [],
			receiver_name: [],
			source_location: [],
			destination_location: [],
		};
		if (filteredData) {
			let cancelled = 0;
			let issued = 0;
			filteredData?.forEach((row) => {
				if (row.soft_delete) {
					cancelled += 1;
				} else {
					issued += 1;
				}
				Object.keys(filterSet).forEach((key) => {
					if (!filterSet[key].includes(row[key].trim())) {
						const keyVal = row[key].trim();
						filterSet[key].push(keyVal);
					}
				});
			});
			const updateStatus = () => setStatusCount({ issued, cancelled });

			updateStatus();
		}
		setFilters(filterSet);
	}, [filteredData]);

	useEffect(() => {
		if (printVisible) {
			if (!isPDFPrint) {
				printLR();
			} else {
				generatePDF(LRPrintRef, {
					filename: `CSN${String(selectedID).padStart(6, '0')}.pdf`,
					...pdfOptions,
				});
				setModalOpen(false);
				setPDFPrint(false);
			}
			setPrintVisible(false);
		}
		if (!isModalOpen && (showPrint || warnCancel)) {
			setTimeout(() => {
				setWarnCancel(false);
				setPDFPrint(false);
				setShowPrint(false);
			}, 100);
		}
	}, [printVisible, isPDFPrint, selectedID, isModalOpen, printLR, showPrint, warnCancel]);

	function onClose() {
		setModalOpen(false);
		setWarnCancel(false);
	}

	const handleChange = useCallback((pagination, filters, sorter) => {
		setColFilters(filters);
	}, []);

	function clearFilter(column) {
		setColFilters({ ...colFilters, [column]: [] });
		setQueryParams({
			...queryParams,
			from_date: dateRange.startDate,
			to_date: dateRange.endDate,
		});
	}

	function handleExport() {
		const { search_type, offset, limit, currentPage, ...restPaginateConfig } = queryParams;
		const data = {
			report_type: 'lr_export',
			file_type: 'csv',
			data: {
				...restPaginateConfig,
				from_date: dateRange.startDate.startOf('days'),
				to_date: dateRange.endDate.endOf('days'),
				url_type: 'download',
			},
		};
		dispatch(
			exportSlice({
				data,
			})
		);
	}

	function clearAllFilters() {
		const resetDateRange = getTerminalDates({ init: true });
		setDateRange(resetDateRange);
		setDataDateRange(resetDateRange);
		setColFilters({});
		setQfilt('');
		setFilterChecked(false);
		setQueryParams({
			...queryParams,
			from_date: resetDateRange.startDate.startOf('days'),
			to_date: resetDateRange.endDate.endOf('days'),
		});
	}

	function gatherData(id, type) {
		setSelectedID(id);
		const selectedLR = filteredData?.filter((item) => item.id === id);
		setSelectedLRDetails(transformConsignmentNoteRow(selectedLR[0]));
		switch (type) {
			case 'print':
				setPrintVisible(true);
				return null;
			case 'view':
				setShowPrint(true);
				setModalOpen(true);
				return null;
			case 'download':
				setPDFPrint(true);
				setPrintVisible(true);
				return null;
			case 'cancel':
				setWarnCancel(true);
				setModalOpen(true);
				return null;
			default:
				return null;
		}
	}

	const LRTableConfig = GenerateLRList({
		gatherData,
		filters: pagefilter,
		colFilters,
		clearFilter,
	});

	function handleAction() {
		if (warnCancel && !printVisible) {
			dispatch(deleteConsignment({ id: selectedID })).then((res) => {
				message.info(`Cancelled CSN${String(res.data.id).padStart(6, '0')}.`);
				window.location.reload();
			});
			onClose();
		} else setPrintVisible(true);
	}

	function filterCancelledData(doFilter) {
		if (doFilter) {
			const nonCancelled = filteredData?.filter((item) => item.soft_delete !== true);
			setFilteredData(nonCancelled);
		} else {
			const reducedData = rowData.filter((item) =>
				moment(item.created_at).isBetween(
					dateRange.startDate,
					dateRange.endDate,
					'days',
					'[]'
				)
			);
			setFilteredData(reducedData);
		}
		setFilterChecked(doFilter);
	}

	useEffect(() => {
		const isDateFilterApplied = !(
			dateRange.startDate.isSame(defaultDateRange.startDate, 'day') &&
			dateRange.endDate.isSame(defaultDateRange.endDate, 'day')
		);
		const areColumnFiltersApplied = Object.values(colFilters).some(
			(filter) => filter && filter.length > 0
		);
		const isQuickFilterApplied = selectQfilt !== '';

		setAreFiltersApplied(
			isDateFilterApplied || areColumnFiltersApplied || isQuickFilterApplied
		);
	}, [dateRange, colFilters, selectQfilt, defaultDateRange]);

	console.log(filteredData);
	return (
		<>
			<div
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					flexWrap: 'nowrap',
					gap: '10px',
					width: '10%',
				}}
			>
				<div style={{ flex: '0 0 auto' }}>
					<LtSearchBar
						enterButton="Search"
						placeholder="Search"
						allowClear
						onSearch={(val) => {
							setQueryParams({
								...queryParams,
								query_str: val,
								page: 1,
							});
						}}
					/>
				</div>
				<div style={{ flex: '0 0 auto' }}>
					<Space direction="vertical">
						<DateRangePicker
							scope={SCOPEDDAYS}
							dateset={dateRange}
							setdate={setDateRange}
							updatefn={FilterData}
							clearAllFilters={clearAllFilters}
							areFiltersApplied={areFiltersApplied}
						/>
						<QuickFilters
							setDateRange={setDateRange}
							selectQfilt={selectQfilt}
							setQfilt={setQfilt}
							updatefn={FilterData}
						/>
					</Space>
				</div>
				<div style={{ flex: '0 0 auto' }}>
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							gap: '10px',
							marginBottom: '5px',
						}}
					>
						<Space direction="horizontal" size="small">
							<Text>Cancelled</Text>
							<Switch
								checkedChildren="Hide"
								unCheckedChildren="Show"
								style={{ width: '60px' }}
								onChange={filterCancelledData}
								checked={filterChecked}
							/>
						</Space>
						<ButtonWrapper
							onClick={() => window.location.reload()}
							icons={<FilterOutlined />}
						>
							RESET FILTER
						</ButtonWrapper>
						<ACL action="generate_report" module="lr_export">
							<ButtonWrapper
								style={{ marginLeft: '-25px' }}
								onClick={() => {
									handleExport();
								}}
								icons={<ExportOutlined />}
							>
								EXPORT
							</ButtonWrapper>
						</ACL>
						{/* <ACL action="create" module="consignment_note">
							<ButtonWrapper
								onClick={() => navigate('/lr/add')}
								icons={<PlusOutlined />}
								variant="ghost"
							>
								CREATE
							</ButtonWrapper>
						</ACL> */}
					</div>
					<div>
						<CountStatus count={statusCount} />
					</div>
				</div>
			</div>
			<Box mt={3}>
				<AntdTable
					rowKey="id"
					dataSource={filteredData}
					columns={LRTableConfig}
					rowClassName={(record) => record.soft_delete && 'custom-disabled-table-row'}
					onChange={handleChange}
				/>
			</Box>

			<Modal
				open={isModalOpen}
				width={warnCancel ? 500 : 1500}
				centered
				onCancel={onClose}
				footer={
					<div>
						{!selectLRDetails.soft_delete &&
							(warnCancel ? (
								<Button key="submit" type="primary" onClick={handleAction}>
									Confirm
								</Button>
							) : (
								<>
									<Button key="submit" type="primary" onClick={handleAction}>
										Print
									</Button>
									<Button
										key="submit"
										type="primary"
										onClick={() => gatherData(selectedID, 'download')}
									>
										Download
									</Button>
								</>
							))}
						<Button key="cancel" onClick={onClose}>
							Cancel
						</Button>
					</div>
				}
			>
				{showPrint && (
					<div style={{ overflow: 'scroll', height: '80vh' }}>
						<LRCopy details={selectLRDetails} />
					</div>
				)}
				{warnCancel && (
					<Result
						status="warning"
						title="Cancel Consignment Note?"
						subTitle={`You are cancelling consignment no. CSN${String(
							selectedID
						).padStart(6, '0')} of ${selectLRDetails.sender_name} from ${
							selectLRDetails.source_location
						} to ${selectLRDetails.destination_location}`}
					/>
				)}
			</Modal>
			{printVisible && (
				<div
					style={{
						display: !printVisible ? 'none' : '',
						position: 'absolute',
						zIndex: '-1',
					}}
				>
					<LRPrint
						details={selectLRDetails}
						ref={LRPrintRef}
						isvisible={+printVisible}
						ispdf={+isPDFPrint}
					/>
				</div>
			)}
		</>
	);
}
